import { axios, baseUrlForService, blueApi } from '@/axios.config';
import * as base64 from '@/lib/base64';
import { debounce } from '@/lib/tools';
import { mockAdapter } from '@/mocks/axiosMockAdapter.config';
import { parseGrpcData } from '@alphauslabs/grpc-chunk-parser';
import moment from 'moment';
import Vue from 'vue';

export const state = {
  reports: {},
  requests: {},

  // Conditions
  owner: '',
  ownerId: '',
  start: null,
  end: null,
  category: '',
  granularity: '',
  resource: '',
  vendor: '',
  selectedTagKey: null,
};

export const hash = ({ owner, ownerId, granularity, from, to, category, vendor }) => {
  owner = ownerId ? `${owner}-${ownerId}` : `${owner}`;
  from = moment(from).format('YYYY-MM-DD');
  to = moment(to).format('YYYY-MM-DD');

  return `${owner}-${granularity}-${from}-${to}-${category}-${vendor}`;
};

/**
 * Generate URL Function
 * @param {string} resourceType - account | tag
 * @param {string} owner - account | group | tagresource
 * @param {string} ownerId
 * @param {string} granularity - daily | monthly
 * @returns {string}
 */
const getUrl = ({ resource, owner }) => {
  if (resource === 'account' || resource === 'group') {
    if (owner === 'company') {
      // For Dashboard graph
      // return `/reports/company/${granularity}`;
      return `dashboard-graph`;
    } else {
      // For Usage Report graph
      // return `/reports/${owner}/${ownerId}/${granularity}`;
      return `usage-report-graph`;
    }
  }

  if (resource === 'cross_tag' || resource === 'tag') {
    if (owner === 'company') {
      // For Dashboard graph
      return `crosstag-dashboard-graph`;
    } else {
      // For Usage Report graph
      return `crosstag-usage-report-graph`;
    }
  }
};

export const actions = {
  async fetch({ state, commit, dispatch, getters }, dashboard = null) {
    // Request Hash
    const reqHash = getters.requestHash(dashboard);

    // Conditions
    const resource = dashboard ? dashboard.resource : state.resource;
    if (resource === 'account' || resource === 'group' || resource === 'azure_subscription') {
      return dispatch('getMonthlyData', dashboard);
    }
    const owner = dashboard ? dashboard.owner : state.owner;
    const ownerId = dashboard ? dashboard.ownerId : state.ownerId;
    const granularity = dashboard ? dashboard.granularity : state.granularity;
    const category = dashboard ? dashboard.category : state.category;
    const vendor = dashboard ? dashboard.vendor : state.vendor;
    const start = moment(dashboard ? dashboard.start : state.start).format('YYYY-MM-DD');
    const end = moment(dashboard ? dashboard.end : state.end).format('YYYY-MM-DD');

    const url = getUrl({
      resource: resource,
      owner: owner,
      // ownerId: ownerId,
      granularity: granularity,
    });

    if (url === 'dashboard-graph' || url === 'usage-report-graph') {
      return dispatch('getMonthlyData', dashboard);
    }

    const params = {
      from: start,
      to: end,
      by: category,
      vendor: vendor,
    };

    /*
     * [Only For Cross Tag]
     * If resource is cross_tag,
     * Need to add 'target_resource_id' key with tag ID.
     */
    if (resource === 'cross_tag' || resource === 'tag') {
      params.target_resource_id = ownerId;
    }

    mockAdapter(axios, 'get', url, 'getChartReportsService', 200);

    // TODO: begin here
    if (url === 'crosstag-dashboard-graph' || url === 'crosstag-usage-report-graph') {
      return dispatch('getMonthlyDataForTag', dashboard);
    }

    commit('requestStart', { reqHash });

    return axios
      .get(url, {
        params: params,
      })
      .then(({ data }) => {
        commit('requestSucceed', { reqHash });
        return dispatch('formatReports', { data, dashboard });
      })
      .then(formattedData => {
        commit('updateReports', { formattedData, reqHash });
      })
      .catch(err => {
        if (axios.isCancel(err)) {
          commit('requestCanceled', { reqHash });
          return;
        }

        commit('requestFailed', { reqHash, err });
        throw err;
      });
  },

  // Resetting from tags to non-tags category
  resetTagState({ commit }) {
    commit('RESET_TAG_STATE');
  },

  async fetchGraphData(
    { state, rootState, commit, dispatch, getters, rootGetters },
    dashboard = null
  ) {
    const reqHash = getters.requestHash(dashboard);
    const resourceType = dashboard ? dashboard.resource : state.resource;
    const owner = dashboard ? dashboard.owner : state.owner;
    const ownerId = dashboard ? dashboard.ownerId : state.ownerId;
    const dimension = dashboard ? dashboard.granularity : state.granularity;
    const subject = dashboard ? dashboard.category : state.category;
    const vendor = dashboard ? dashboard.vendor : state.vendor;
    let from = moment(dashboard ? dashboard.start : state.start).format('YYYYMMDD');
    const to = moment(dashboard ? dashboard.end : state.end).format('YYYYMMDD');

    // Reset tag state if we're not in tags category
    if (subject !== 'tags' && state.selectedTagKey) {
      commit('RESET_TAG_STATE');
    }

    // Validate tag selection when in tags category
    if (subject === 'tags' && !state.selectedTagKey) {
      console.log('No tag key selected for tags category');
      return;
    }

    // Case: daily
    // APIがデータレンジ2ヶ月間しかサポートしていないため、月別から日別に変えた際、
    // 2ヶ月以上のレンジの場合は自動で2ヶ月間に変更する。
    if (dimension === 'daily') {
      if (moment(to).diff(from, 'months', true) > 2) {
        from = moment(to)
          .subtract(2, 'month')
          .format('YYYYMMDD');
      }
    }

    // daily or monthly, rangeをSTATEにセット
    commit('updateDimension', dimension);
    commit('updateRange', { start: from, end: to });

    let url = '';
    let params = {};
    if (
      resourceType === 'account' ||
      resourceType === 'group' ||
      resourceType === 'azure_subscription'
    ) {
      url = `cost/v1/${vendor}/costs:read`;
      params = {
        startTime: from,
        endTime: to,
      };

      // Gab: added region, instanceType, usageType(hidden) and tags for awsOptions
      switch (vendor) {
        case 'aws':
          params.awsOptions = {
            groupByColumns: 'account,productCode,serviceCode,region,instanceType',
            groupByMonth: dimension === 'monthly',
          };

          if (subject === 'tags' && state.selectedTagKey) {
            params.awsOptions = {
              groupByColumns: 'tags',
              groupByTagKey: state.selectedTagKey,
              groupAccounts: true,
            };
          }
          break;
        case 'azure':
          params.azureOptions = {
            groupByColumns: 'account,serviceName',
            groupByMonth: dimension === 'monthly',
          };
          break;
        case 'gcp':
          params.gcpOptions = {
            groupByColumns: 'account',
            groupByMonth: dimension === 'monthly',
          };
          break;
      }
      if (owner === 'company') {
        params.groupId = rootState.userState.companyId;
        if (subject === 'productCode') {
          switch (vendor) {
            case 'aws':
              params.awsOptions = {
                groupByColumns: 'productCode',
                groupAccounts: true,
                groupByMonth: dimension === 'monthly',
              };
              break;
          }
        } else if (subject === 'service') {
          switch (vendor) {
            case 'aws':
              params.awsOptions = {
                groupByColumns: 'serviceCode',
                groupAccounts: true,
                groupByMonth: dimension === 'monthly',
              };
              break;
            case 'azure':
              params.azureOptions = {
                groupByColumns: 'serviceName',
              };
              break;
            case 'gcp':
              params.gcpOptions = {
                groupByColumns: 'service',
              };
              break;
          }
        } else if (subject === 'account') {
          switch (vendor) {
            case 'aws':
              params.awsOptions = {
                groupByColumns: 'none',
                groupByMonth: dimension === 'monthly',
              };
              break;
            case 'azure':
              params.azureOptions = {
                groupByColumns: 'account',
              };
              break;
            case 'gcp':
              params.gcpOptions = {
                groupByColumns: 'none',
              };
              break;
          }
        } else if (subject === 'region') {
          switch (vendor) {
            case 'aws':
              params.awsOptions = {
                groupByColumns: 'region',
                groupAccounts: true,
                groupByMonth: dimension === 'monthly',
              };
              break;
          }
        }

        // Hidden for a while
        // else if (subject === 'usageType') {
        //   switch (vendor) {
        //     case 'aws':
        //       params.awsOptions = {
        //         groupByColumns: 'usageType',
        //         groupAccounts: true,
        //         groupByMonth: dimension === 'monthly',
        //       };
        //       break;
        //   }
        // }

        else if (subject === 'instanceType') {
          switch (vendor) {
            case 'aws':
              params.awsOptions = {
                groupByColumns: 'instanceType',
                groupAccounts: true,
                groupByMonth: dimension === 'monthly',
              };
              break;
          }
        } else if (subject === 'tags') {
          const selectedTagKey = state.selectedTagKey;
          switch (vendor) {
            case 'aws':
              params.awsOptions = {
                groupByColumns: 'tags',
                groupAccounts: true,
                groupByMonth: dimension === 'monthly',
                groupByTagKey: selectedTagKey,
              };
              break;
          }
        }
      } else if (ownerId) {
        params.accountId = ownerId.split('|')[0];
        if (vendor === 'azure') {
          if (subject === 'service') {
            params.azureOptions = {
              groupByColumns: 'serviceName',
              groupByMonth: dimension === 'monthly',
            };
          }
          if (subject === 'account') {
            params.azureOptions = {
              groupByColumns: 'account',
              groupByMonth: dimension === 'monthly',
            };
          }
        }
      }
    }

    if (resourceType === 'cross_tag' || resourceType === 'tag') {
      url = `cost/v1/${vendor}/tagcosts:read`;
      let groupByColumns =
        subject === 'resource' ? 'satisfier,satisfierType' : 'productCode,serviceCode';
      params = {
        billingInternalId: rootState.userState.companyId,
        startTime: from,
        endTime: to,
        groupByMonths: dimension === 'monthly',
        awsOptions: {
          tagId: ownerId,
          groupByColumns: groupByColumns,
        },
      };
      // console.log('[cross-tag][fetch] params: ', JSON.stringify(params));
    }

    if (resourceType === 'report_filter') {
      url = `cost/v1/${vendor}/costs:read`;
      const activeFilterId = rootGetters['reportFilters/getActiveFilter'];
      const filterData = rootGetters['reportFilters/getActiveFilterData']({
        vendor: rootState.vendor,
        filterId: activeFilterId,
      });
      const { accountId, groupId, awsOptions } = filterData;
      url = `cost/v1/${vendor}/costs:read`;
      params = {
        startTime: from,
        endTime: to,
        awsOptions: {
          ...awsOptions,
          groupByMonth: false,
          tagFilters: awsOptions.includeTags ? awsOptions.tagFilters : [],
        },
        groupId,
        accountId,
      };
    }

    const allRecords = [];

    commit('requestStart', { reqHash });

    // The UI will wait for the debounced function to finish before updating the UI
    const updateUI = debounce(
      ({ formattedData, reqHash }) => commit('updateReports', { formattedData, reqHash }),
      200
    );

    return parseGrpcData(
      {
        url: baseUrlForService('blue') + url,
        method: 'POST',
        headers: {
          Authorization: blueApi.defaults.headers.common['Authorization'],
        },
        body: params,
      },
      {
        limiter: 200,
        concatData: false,
      },
      async data => {
        allRecords.push(...data);
        let graphData = {};
        graphData[vendor] = generateGraphDataV2(
          rootState,
          vendor,
          resourceType,
          dimension,
          subject,
          allRecords
        );

        // disable loading as soon as get the data response,
        // the graph will continue update when receiving the data

        // I disabled this as part of chart load problem.
        // commit('requestSucceed', { reqHash });

        if (resourceType === 'report_filter') {
          updateUI({ formattedData: graphData[vendor], reqHash });
        } else {
          const formattedData = await dispatch('formatReports', { data: graphData, dashboard });
          updateUI({ formattedData, reqHash });
        }
      },
      async () => {
        commit('requestSucceed', { reqHash });
      },
      err => {
        commit('requestFailed', { reqHash, err });
        throw err;
      }
    );
  },

  async formatReports({ state }, { data, dashboard = null }) {
    /*
     *  [fn] Add total data to reports data - Temporary
     */
    const addTotalData = data => {
      const value1Key = 'true_unblended_cost';
      const value2Key = 'unblended_cost';
      const value3Key = 'blended_cost';
      const totalId = '#total';
      const totalName = 'Total';

      // Date arrayを作成
      const newArr = [];
      data.forEach(i => {
        i.date.forEach(j => {
          if (!newArr.find(k => k.date === j.date)) {
            newArr.push({
              date: j.date,
              [value1Key]: 0,
              [value2Key]: 0,
              [value3Key]: 0,
            });
          }
        });
      });

      // Total dataを作成
      newArr.forEach(i => {
        data.forEach(j => {
          const target = j.date.find(k => k.date === i.date);
          if (target) {
            i[value1Key] += target[value1Key];
            i[value2Key] += target[value2Key];
            i[value3Key] += target[value3Key];
          }
        });
      });

      const total = {
        id: totalId,
        name: totalName,
        date: newArr,
      };

      return [total, ...data];
    };

    /*
     * [fn] Convert data - for Performance
     */
    const convertDateToUnix = data => {
      data.forEach(i => {
        i.date.forEach(d => {
          d.date = moment(d.date).toDate();
        });
      });

      return data;
    };

    /*
     * [Fn] If id has "(" or ")", replace to "-" or "".
     */
    const formatId = data => {
      data.forEach(i => {
        if (i.id && (i.id.includes('(') || i.id.includes(')'))) {
          i.id = i.id.replace('(', '-');
          i.id = i.id.replace(')', '');
        }
      });

      return data;
    };

    const vendor = dashboard ? dashboard.vendor : state.vendor;
    data = data[vendor];
    data = formatId(data);

    let formattedData = data.length ? convertDateToUnix(addTotalData(data)) : [];

    return formattedData;
  },

  async getMonthlyData({ rootState, getters, commit, dispatch }, dashboard = null) {
    // Request Hash
    const reqHash = getters.requestHash(dashboard);

    // Conditions
    const ownerId = dashboard ? dashboard.ownerId : state.ownerId;
    const owner = dashboard ? dashboard.owner : state.owner;
    const vendor = dashboard ? dashboard.vendor : state.vendor;
    const resourceType = dashboard ? dashboard.resource : state.resource;
    const dimension = dashboard ? dashboard.granularity : state.granularity;
    const subject = dashboard ? dashboard.category : state.category;
    let from = moment(dashboard ? dashboard.start : state.start).format('YYYYMMDD');
    const to = moment(dashboard ? dashboard.end : state.end).format('YYYYMMDD');

    let params = {
      startTime: from,
      endTime: to,
    };

    switch (vendor) {
      case 'aws':
        params.awsOptions = {
          groupByColumns: dimension === 'monthly' ? '' : 'account,productCode',
          groupByMonth: dimension === 'monthly',
        };
        break;
      case 'azure':
        params.azureOptions = {
          groupByColumns: 'account,serviceName',
          groupByMonth: dimension === 'monthly',
        };
        break;
      case 'gcp':
        params.gcpOptions = {
          groupByColumns: dimension === 'monthly' ? '' : 'account',
          groupByMonth: dimension === 'monthly',
        };
        break;
    }
    if (owner === 'company') {
      params.groupId = rootState.userState.companyId;
      if (dimension === 'daily') {
        if (subject === 'service') {
          switch (vendor) {
            case 'aws':
              params.awsOptions = {
                groupByColumns: dimension === 'monthly' ? '' : 'productCode',
                groupAccounts: true,
              };
              break;
            case 'azure':
              params.azureOptions = {
                groupByColumns: 'serviceName',
              };
              break;
            case 'gcp':
              params.gcpOptions = {
                groupByColumns: dimension === 'monthly' ? '' : 'service',
                groupAccounts: true,
              };
              break;
          }
        } else if (subject === 'account') {
          switch (vendor) {
            case 'aws':
              params.awsOptions = {
                groupByColumns: dimension === 'monthly' ? '' : 'none',
              };
              break;
            case 'azure':
              params.azureOptions = {
                groupByColumns: 'account',
              };
              break;
            case 'gcp':
              params.gcpOptions = {
                groupByColumns: dimension === 'monthly' ? '' : 'none',
              };
              break;
          }
        }
      }
    } else if (ownerId) {
      params.accountId = ownerId.split('|')[0];
      if (vendor === 'azure') {
        if (subject === 'service') {
          params.azureOptions = {
            groupByColumns: 'serviceName',
            groupByMonth: dimension === 'monthly',
          };
        }
        if (subject === 'account') {
          params.azureOptions = {
            groupByColumns: 'account',
            groupByMonth: dimension === 'monthly',
          };
        }
      }
    }

    const url = `cost/v1/${vendor}/costs:read`;

    const allRecords = [];

    commit('requestStart', { reqHash });

    // The UI will wait for the debounced function to finish before updating the UI
    const updateUI = debounce(
      ({ formattedData, reqHash }) => commit('updateReports', { formattedData, reqHash }),
      200
    );

    // console.log('[getMonthlyData] -> params: ', JSON.stringify(params));

    return parseGrpcData(
      {
        url: baseUrlForService('blue') + url,
        method: 'POST',
        headers: {
          Authorization: blueApi.defaults.headers.common['Authorization'],
        },
        body: params,
      },
      {
        limiter: 200,
        concatData: false,
      },
      async data => {
        allRecords.push(...data);
        let graphData = {};
        graphData[vendor] = generateGraphDataV2(
          rootState,
          vendor,
          resourceType,
          dimension,
          subject,
          allRecords
        );

        if (resourceType === 'report_filter') {
          updateUI({ formattedData: graphData[vendor], reqHash });
        } else {
          const formattedData = await dispatch('formatReports', { data: graphData, dashboard });
          updateUI({ formattedData, reqHash });
        }
      },
      async () => {
        commit('requestSucceed', { reqHash });
      },
      err => {
        commit('requestFailed', { reqHash, err });
        throw err;
      }
    );
  },

  async getMonthlyDataForTag({ rootState, getters, commit, dispatch }, dashboard = null) {
    // Request Hash
    const reqHash = getters.requestHash(dashboard);

    // Conditions
    const ownerId = dashboard ? dashboard.ownerId : state.ownerId;
    // const owner = dashboard ? dashboard.owner : state.owner;
    const vendor = dashboard ? dashboard.vendor : state.vendor;
    const resourceType = dashboard ? dashboard.resource : state.resource;
    const dimension = dashboard ? dashboard.granularity : state.granularity;
    const subject = dashboard ? dashboard.category : state.category;
    let from = moment(dashboard ? dashboard.start : state.start).format('YYYYMMDD');
    const to = moment(dashboard ? dashboard.end : state.end).format('YYYYMMDD');

    let params = {
      startTime: from,
      endTime: to,
      groupByMonths: dimension === 'monthly',
      // billingInternalId: rootState.userState.companyId,
    };

    params.target_resource_id = ownerId;
    params.billingInternalId = rootState.userState.companyId;

    let groupByColumns =
      subject === 'resource' ? 'satisfier,satisfierType' : 'productCode,serviceCode';

    switch (vendor) {
      case 'aws':
        params.awsOptions = {
          tagId: '',
          groupByColumns,
        };
        if (ownerId) {
          params.awsOptions = {
            tagId: ownerId,
          };
        }
        break;
      case 'azure':
        // TODO: implement later
        break;
    }

    const url = `cost/v1/${vendor}/tagcosts:read`;

    // console.log('[getMonthlyDataForTag] params: ', JSON.stringify(params));

    const allRecords = [];

    commit('requestStart', { reqHash });

    // The UI will wait for the debounced function to finish before updating the UI
    const updateUI = debounce(
      ({ formattedData, reqHash }) => commit('updateReports', { formattedData, reqHash }),
      200
    );

    return parseGrpcData(
      {
        url: baseUrlForService('blue') + url,
        method: 'POST',
        headers: {
          Authorization: blueApi.defaults.headers.common['Authorization'],
        },
        body: params,
      },
      {
        limiter: 200,
        concatData: false,
      },
      async data => {
        // console.log('[getMonthlyDataForTag] data: ', data);
        allRecords.push(...data);
        let graphData = {};
        graphData[vendor] = generateGraphDataV2(
          rootState,
          vendor,
          resourceType,
          dimension,
          subject,
          allRecords
        );

        if (resourceType === 'report_filter') {
          updateUI({ formattedData: graphData[vendor], reqHash });
        } else {
          const formattedData = await dispatch('formatReports', { data: graphData, dashboard });
          // console.log('[getMonthlyDataForTag] formattedData: ', formattedData);
          updateUI({ formattedData, reqHash });
        }
      },
      async () => {
        commit('requestSucceed', { reqHash });
      },
      err => {
        commit('requestFailed', { reqHash, err });
        throw err;
      }
    );
  },

  // GAB: Changes here
  setSelectedTagKey({ commit }, tagKey) {
    commit('setSelectedTagKey', tagKey);
  },
};

export const mutations = {
  // GAB: Update the state with the selected tag key
  setSelectedTagKey(state, tagKey) {
    state.selectedTagKey = tagKey;
  },

  // Gab: Resetting Tag state if categories changes
  RESET_TAG_STATE(state) {
    state.selectedTagKey = null;
  },

  updateReports(state, { formattedData, reqHash }) {
    Vue.set(state.reports, reqHash, formattedData);
  },

  // Gab: Tag clean up
  updateCondition(state, { key, value }) {
    if (key === 'category' && value !== 'tags') {
      state.selectedTagKey = null;
    }
    Vue.set(state, key, value);
  },

  requestStart(state, { reqHash, cancelToken }) {
    Vue.set(state.requests, reqHash, {
      inProgress: true,
      error: null,
      canceled: false,
      cancelToken,
    });
  },

  requestSucceed(state, { reqHash }) {
    Vue.set(state.requests, reqHash, {
      inProgress: false,
      error: null,
      canceled: false,
      cancelToken: null,
    });
  },

  requestCanceled(state, { reqHash }) {
    Vue.set(state.requests, reqHash, {
      inProgress: false,
      error: null,
      canceled: true,
      cancelToken: null,
    });
  },

  requestFailed(state, { reqHash, err }) {
    Vue.set(state.requests, reqHash, {
      inProgress: false,
      error: err,
      canceled: false,
      cancelToken: null,
    });
  },
};

export const getters = {
  requestHash: state => (dashboard = null) => {
    const owner = dashboard ? dashboard.owner : state.owner;
    const ownerId = dashboard ? dashboard.ownerId : state.ownerId;
    const vendor = dashboard ? dashboard.vendor : state.vendor;
    const start = dashboard ? dashboard.start : state.start;
    const end = dashboard ? dashboard.end : state.end;
    const granularity = dashboard ? dashboard.granularity : state.granularity;
    const category = dashboard ? dashboard.category : state.category;
    const resource = dashboard ? dashboard.resource : state.resource;

    return hash({
      owner: owner,
      ownerId: ownerId,
      granularity: granularity,
      from: start,
      to: end,
      category: category,
      resource: resource,
      vendor: vendor,
    });
  },

  getData: (state, getters) => (dashboard = null) => {
    const hash = getters.requestHash(dashboard);
    return state.reports[hash] ? state.reports[hash] : [];
  },

  // GAB: Changes here
  getSelectedTagKey(state) {
    return state.selectedTagKey;
  },
};

/**
 * Generate graph data.
 * @param {string} vendor
 * @param {string} resourceType
 * @param {string} dimension
 * @param {string} subject
 * @param {Array} records
 * @returns {Array} An array object with format:
 * 1. Subject: service - Dimension: daily
 * [
 *   {
 *     id: 'AWSCloudTrail',
 *     name: 'AWSCloudTrail',
 *     date: [
 *       {
 *         blended_cost: 35.78906349999999
 *         unblended_cost: 35.78906349999999
 *         true_unblended_cost: 35.78906349999999
 *         date: '2022-01-22' // for monthly, it is '2022-01'
 *       }
 *     ]
 *   },
 *   {
 *     id: 'AWSDataTransfer',
 *     name: 'AWSDataTransfer',
 *     date: [
 *       {
 *         blended_cost: 69.64238871739917
 *         unblended_cost: 69.64238871739917
 *         true_unblended_cost: 69.64238871739917
 *         date: '2022-01-22' // for monthly, it is '2022-01'
 *       }
 *     ]
 *   },
 * ]
 *
 * 2. Subject: account - Dimension: daily
 * [
 *   {
 *     id: '131920598436',
 *     name: '131920598436',
 *     date: [
 *       {
 *         blended_cost: 35.78906349999999
 *         unblended_cost: 35.78906349999999
 *         true_unblended_cost: 35.78906349999999
 *         date: '2022-01-22' // for monthly, it is '2022-01'
 *       }
 *     ]
 *   },
 *   {
 *     id: '123450598436',
 *     name: '123450598436',
 *     date: [
 *       {
 *         blended_cost: 69.64238871739917
 *         unblended_cost: 69.64238871739917
 *         true_unblended_cost: 69.64238871739917
 *         date: '2022-01-22' // for monthly, it is '2022-01'
 *       }
 *     ]
 *   },
 * ]
 *
 * From input format:
 * {"result":{"aws":{"account":"131920598436","groupId":"yH11B","type":"","date":"2022-01-22T00:00:00Z","productCode":"AWSCloudTrail","serviceCode":"","region":"","zone":"","usageType":"","instanceType":"","operation":"","invoiceId":"","description":"","resourceId":"","tags":{} "costCategories":{},"usage":0,"cost":35.78906349999999,"unblendedCost":0,"baseCurrency":"USD","exchangeRate":0,"targetCost":0,"targetUnblendedCost":0,"targetCurrency":"","effectiveCost":0,"targetEffectiveCost":0,"amortizedCost":0,"targetAmortizedCost":0,"tagId":""}}}
 * {"result":{"aws":{"account":"123450598436","groupId":"yH11B","type":"","date":"2022-01-22T00:00:00Z","productCode":"AWSDataTransfer","serviceCode":"","region":"","zone":"","usageType":"","instanceType":"","operation":"","invoiceId":"","description":"","resourceId":"","tags":{},"costCategories":{},"usage":0,"cost":69.64238871739917,"unblendedCost":0,"baseCurrency":"USD","exchangeRate":0,"targetCost":0,"targetUnblendedCost":0,"targetCurrency":"","effectiveCost":0,"targetEffectiveCost":0,"amortizedCost":0,"targetAmortizedCost":0,"tagId":""}}}
 */
// const generateGraphData = (rootState, vendor, resourceType, dimension, subject, records) => {
//   const repGrps = [];
//   const grpMap = {};
//   const dateMap = {};
//
//   // Generate account map to know account name for `subject` === `account`
//   let accountMap = {};
//   if (subject === 'account') {
//     let accounts = [];
//     const accountsModule = rootState.accounts;
//     if (accountsModule) {
//       if (accountsModule.accounts.data[vendor]) {
//         accounts = accountsModule.accounts.data[vendor];
//       }
//     }
//     for (const account of accounts) {
//       accountMap[account['account_id']] = account['account_name'];
//     }
//   }
//
//   // Group records by dimension and subject.
//   for (const record of records) {
//     if (!record) {
//       continue;
//     }
//     if (!record.result) {
//       continue;
//     }
//     const cost = record.result[vendor];
//     if (!cost) {
//       continue;
//     }
//
//     const dt = moment(cost.date);
//     const date = dimension === 'daily' ? dt.format('YYYY-MM-DD') : dt.format('YYYY-MM');
//
//     if (resourceType === 'report_filter') {
//       if (dateMap[date]) {
//         dateMap[date].push({
//           ...cost,
//           unblended_cost: cost.cost,
//           true_unblended_cost: cost.cost,
//           blended_cost: cost.cost,
//         });
//       } else {
//         dateMap[date] = [];
//       }
//     } else {
//       let grpKey = '';
//       let name = '';
//
//       switch (subject) {
//         case 'account': {
//           const accountName = accountMap[cost.account];
//           grpKey = cost.account;
//           name = accountName ? accountName : cost.account;
//           break;
//         }
//         case 'service': {
//           grpKey = cost.productCode;
//           name = cost.productCode;
//           break;
//         }
//         case 'resource': {
//           if (cost.tagId) {
//             const decodedTagId = base64.decode(cost.tagId);
//             const tagObj = JSON.parse(decodedTagId);
//             const kv = tagObj.tags[0];
//             const code = `${kv.key}: ${kv.value} (${cost.account})`;
//             grpKey = code;
//             name = code;
//           }
//           break;
//         }
//         default:
//           break;
//       }
//       let grp = grpMap[grpKey];
//
//       if (!grp) {
//         grp = {
//           id: grpKey,
//           name: name,
//           dateMap: {},
//         };
//       }
//       if (grp.dateMap[date]) {
//         grp.dateMap[date].true_unblended_cost += cost.cost;
//         grp.dateMap[date].unblended_cost += cost.cost;
//         grp.dateMap[date].blended_cost += cost.cost;
//       } else {
//         grp.dateMap[date] = {
//           date: date,
//           unblended_cost: cost.cost,
//           true_unblended_cost: cost.cost,
//           blended_cost: cost.cost,
//         };
//       }
//       grpMap[grpKey] = grp;
//     }
//   }
//   if (resourceType === 'report_filter') {
//     return dateMap;
//   } else {
//     // Convert map to array and sort.
//     if (Object.keys(grpMap).length === 0) {
//       return [];
//     }
//     for (const grpKey in grpMap) {
//       const grp = grpMap[grpKey];
//       grp['date'] = [];
//       for (const date in grp.dateMap) {
//         const cost = grp.dateMap[date];
//         grp.date.push(cost);
//       }
//       grp.date.sort((a, b) => {
//         const left = '' + a.date;
//         const right = '' + b.date;
//         return left.localeCompare(right);
//       });
//       repGrps.push(grp);
//     }
//     repGrps.sort((a, b) => {
//       const left = '' + a.name;
//       const right = '' + b.name;
//       return left.localeCompare(right);
//     });
//
//     return repGrps;
//   }
// };

const generateGraphDataV2 = (rootState, vendor, resourceType, dimension, subject, records) => {
  const repGrps = [];
  const grpMap = {};
  const dateMap = {};

  // Generate account map to know account name for `subject` === `account`
  let accountMap = {};
  let azureAccountIdMap = {};
  if (subject === 'account') {
    let accounts = [];
    const accountsModule = rootState.accounts;
    if (accountsModule) {
      if (accountsModule.accounts.data[vendor]) {
        accounts = accountsModule.accounts.data[vendor];
      }
    }
    for (const account of accounts) {
      accountMap[account['account_id']] = account['account_name'];
      if (vendor === 'azure') {
        const accIds = account['account_ids'].split(',');
        for (const accId of accIds) {
          azureAccountIdMap[accId] = account['account_id'];
        }
      }
    }
  }

  // Group records by dimension and subject.
  for (const record of records) {
    if (!record) {
      continue;
    }
    if (!record) {
      continue;
    }
    const cost = record[vendor];
    if (!cost) {
      continue;
    }

    const dt = moment(cost.date);
    const date = dimension === 'daily' ? dt.format('YYYY-MM-DD') : dt.format('YYYY-MM');

    if (resourceType === 'report_filter') {
      if (dateMap[date]) {
        dateMap[date].push({
          ...cost,
          unblended_cost: cost.cost,
          true_unblended_cost: cost.cost,
          blended_cost: cost.cost,
        });
      } else {
        dateMap[date] = [];
      }
    } else {
      let grpKey = '';
      let name = '';

      switch (subject) {
        case 'account': {
          if (vendor === 'azure') {
            cost.account = azureAccountIdMap[cost.account];
          }
          const accountName = accountMap[cost.account];
          grpKey = cost.account;
          name = accountName ? accountName : cost.account;
          break;
        }
        case 'productCode': {
          if (vendor === 'aws') {
            grpKey = cost.productCode;
            name = cost.productCode;
          }
          break;
        }
        case 'service': {
          if (vendor === 'aws') {
            grpKey = cost.serviceCode;
            name = cost.serviceCode ? cost.serviceCode : 'No Service Name';
          } else if (vendor === 'azure') {
            grpKey = cost.serviceName;
            name = cost.serviceName;
          } else {
            grpKey = cost.service;
            name = cost.service;
          }
          break;
        }
        // GAB: added handler and formatter for region, instanceType, usageType(Hidden) and tags option
        case 'region': {
          if (vendor === 'aws') {
            grpKey = cost.region;
            name = cost.region;
          }
          break;
        }
        case 'instanceType': {
          if (vendor === 'aws') {
            grpKey = cost.instanceType;
            name = cost.instanceType ? cost.instanceType : 'No instance type';
          }
          break;
        }

        // case 'tags': {
        //   if (cost.tags) {
        //     const tagValues = Object.values(cost.tags);  // Extract the tag values (ignore the keys)
        //     tagValues.forEach(tagValue => {
        //       grpKey = tagValue && tagValue.trim() !== '' ? tagValue : 'No tag value';
        //       name = tagValue && tagValue.trim() !== '' ? tagValue : 'No tag value';  // Check for empty string and assign "No tag value"
        //     });
        //   }
        //   break;
        // }

        case 'tags': {
          if (cost.tags) {
            const tagValues = Object.values(cost.tags);
            let hasTagValue = false;

            tagValues.forEach(tagValue => {
              if (tagValue && tagValue.trim() !== '') {
                hasTagValue = true;
                grpKey = tagValue;
                name = tagValue;
              }
            });

            // If no valid tag values, group as "No tag value"
            if (!hasTagValue) {
              grpKey = 'No tag value';
              name = 'No tag value';
            }
          } else {
            // No tag key at all, treat as "No tag value"
            grpKey = 'No tag value';
            name = 'No tag value';
          }
          break;
        }

        // Hidden for a while
        // case 'usageType': {
        //   if (vendor === 'aws') {
        //     grpKey = cost.usageType;
        //     name = cost.usageType;
        //   }
        //   break;
        // }
        case 'resource': {
          if (cost.tagId) {
            const decodedTagId = base64.decode(cost.tagId);
            const tagObj = JSON.parse(decodedTagId);
            const kv = tagObj.tags[0];
            const code = `${kv.key}: ${kv.value} (${cost.account})`;
            grpKey = code;
            name = code;
          }
          break;
        }
        default:
          break;
      }
      let grp = grpMap[grpKey];

      if (!grp) {
        grp = {
          id: grpKey,
          name: name,
          dateMap: {},
        };
      }
      if (grp.dateMap[date]) {
        grp.dateMap[date].true_unblended_cost += cost.cost;
        grp.dateMap[date].unblended_cost += cost.cost;
        grp.dateMap[date].blended_cost += cost.cost;
      } else {
        grp.dateMap[date] = {
          date: date,
          unblended_cost: cost.cost,
          true_unblended_cost: cost.cost,
          blended_cost: cost.cost,
        };
      }
      grpMap[grpKey] = grp;
    }
  }
  if (resourceType === 'report_filter') {
    return dateMap;
  } else {
    // Convert map to array and sort.
    if (Object.keys(grpMap).length === 0) {
      return [];
    }
    for (const grpKey in grpMap) {
      const grp = grpMap[grpKey];
      grp['date'] = [];
      for (const date in grp.dateMap) {
        const cost = grp.dateMap[date];
        grp.date.push(cost);
      }
      grp.date.sort((a, b) => {
        const left = '' + a.date;
        const right = '' + b.date;
        return left.localeCompare(right);
      });
      repGrps.push(grp);
    }
    repGrps.sort((a, b) => {
      const left = '' + a.name;
      const right = '' + b.name;
      return left.localeCompare(right);
    });

    return repGrps;
  }
};
